import supabase from "@/services/supabaseClient";

export const ADMIN_TEAM_ROLES = ["super_admin", "admin", "support", "kyc_reviewer"];

function cleanText(value) {
  return String(value || "").trim();
}

export function normalizeAdminTeamRole(value) {
  const role = cleanText(value).toLowerCase();
  return ADMIN_TEAM_ROLES.includes(role) ? role : "support";
}

export function labelAdminTeamRole(value) {
  const role = normalizeAdminTeamRole(value);
  if (role === "super_admin") return "Super admin";
  if (role === "kyc_reviewer") return "KYC reviewer";
  if (role === "admin") return "Admin";
  return "Support";
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function sha256Hex(value) {
  const encoded = new TextEncoder().encode(String(value || ""));
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return bytesToHex(new Uint8Array(digest));
}

function createInviteToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

function appOrigin() {
  if (typeof window === "undefined") return "https://staging.afrimed.tech";
  return window.location.origin || "https://staging.afrimed.tech";
}

export async function getCurrentAdminTeamRole() {
  const { data, error } = await supabase.rpc("current_admin_team_role", {
    target_user: null,
  });
  if (error) throw error;
  return normalizeAdminTeamRole(data);
}

export async function listAdminTeamMembers() {
  const { data, error } = await supabase
    .from("admin_team_members")
    .select("id,user_id,email,full_name,role,status,created_at,created_by,updated_at,deactivated_at")
    .order("status", { ascending: true })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function upsertAdminTeamMember({
  email = "",
  fullName = "",
  role = "support",
} = {}) {
  const normalizedEmail = cleanText(email).toLowerCase();
  if (!normalizedEmail) throw new Error("Email is required.");
  const inviteToken = createInviteToken();
  const inviteTokenHash = await sha256Hex(inviteToken);
  const inviteExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase.rpc("upsert_admin_team_member", {
    member_email: normalizedEmail,
    member_full_name: cleanText(fullName) || null,
    member_role: normalizeAdminTeamRole(role),
    invite_token_hash: inviteTokenHash,
    invite_expires_at: inviteExpiresAt,
  });
  if (error) throw error;

  const inviteUrl = `${appOrigin()}/admin/team/accept?token=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(normalizedEmail)}`;
  const { data: inviteData, error: inviteError } = await supabase.functions.invoke(
    "send-admin-team-invite",
    {
      body: {
        email: normalizedEmail,
        full_name: cleanText(fullName) || null,
        role: normalizeAdminTeamRole(role),
        invite_url: inviteUrl,
      },
    },
  );
  if (inviteError || inviteData?.ok === false) {
    throw new Error(inviteError?.message || inviteData?.error || "Admin invitation email could not be sent.");
  }

  return data;
}

export async function acceptAdminTeamInvite(token = "") {
  const inviteToken = cleanText(token);
  if (!inviteToken) throw new Error("Invitation token is required.");
  const { data, error } = await supabase.rpc("accept_admin_team_invite", {
    invite_token: inviteToken,
  });
  if (error) throw error;
  return data;
}

export async function getAdminTeamInviteStatus(token = "") {
  const inviteToken = cleanText(token);
  if (!inviteToken) throw new Error("Invitation token is required.");
  const { data, error } = await supabase.rpc("get_admin_team_invite_status", {
    invite_token: inviteToken,
  });
  if (error) throw error;
  return data;
}

export async function declineAdminTeamInvite(token = "") {
  const inviteToken = cleanText(token);
  if (!inviteToken) throw new Error("Invitation token is required.");
  const { data, error } = await supabase.rpc("decline_admin_team_invite", {
    invite_token: inviteToken,
  });
  if (error) throw error;
  return data;
}

export async function deactivateAdminTeamMember(id = "") {
  const memberId = cleanText(id);
  if (!memberId) throw new Error("Admin team member id is required.");

  const { data, error } = await supabase.rpc("deactivate_admin_team_member", {
    member_id: memberId,
  });
  if (error) throw error;

  const { data: notificationData, error: notificationError } = await supabase.functions.invoke(
    "send-admin-team-deactivation",
    {
      body: {
        member_id: memberId,
      },
    },
  );
  if (notificationError || notificationData?.ok === false) {
    throw new Error(
      notificationError?.message ||
        notificationData?.error ||
        "Admin deactivation email could not be sent.",
    );
  }

  return data;
}
